'use client'

import {PersonalForm, TestingTypeForm} from '../ui/form'
import {observer} from 'mobx-react'
import React from 'react'

import {Routes} from '@/src/config/routes'
import theme from '@/src/config/theme'

import {DateForm, ShopForm} from './form'
import {FormProvider} from '@/src/shared/modules/form'
import {FormTestingStep} from '@/src/features/functional-testing/store'
import {
  DateFormSchema,
  PersonalFormSchema,
  ShopFormSchema,
  TestTypeFormSchema,
} from '@/src/(pages)/testing'
import {useFormTesting} from '@/src/features/functional-testing'
import {FormStepsAccordion} from '@/src/features/functional-testing/ui/form-steps-accordion'
import type {FormStepConfig} from '@/src/features/functional-testing/ui/form-steps-accordion'
import styled from 'styled-components'
import {useMediaQuery} from '@/src/shared/hooks/useMediaQuery'
import {useStore} from '@/src/shared/hooks/useStore'
import {getInjection} from '@runlab/di'
import type {FunctionalTestingPageContent} from './functional-testing-screen'

export const FuncTestingContainer = observer(() => {
  const isMobile = useMediaQuery(`(max-width: ${theme.mediaSizes.sm}px)`)

  const {pageData} = useStore<FunctionalTestingPageContent>()
  const {types, labs} = pageData!.content

  const testingFormStore = useFormTesting()

  const typeStepContent = (
    <FormProvider schema={TestTypeFormSchema}>
      <TestingTypeForm />
    </FormProvider>
  )

  const labStepContent = (
    <FormProvider schema={ShopFormSchema}>
      <ShopForm />
    </FormProvider>
  )

  const dateStepContent = (
    <FormProvider schema={DateFormSchema}>
      <DateForm routes={Routes.webApi.form.testing} />
    </FormProvider>
  )

  const personalStepContent = (
    <FormProvider
      schema={TestTypeFormSchema.merge(ShopFormSchema)
        .merge(DateFormSchema)
        .merge(PersonalFormSchema)}
      data={{
        name: pageData?.session?.user?.name,
        last_name: pageData?.session?.user?.last_name,
        phone: pageData?.session?.user?.phone,
        email: pageData?.session?.user?.email,
        agree: true,
        notify_by_sms: true,
        subscribed: true,
      }}>
      <PersonalForm
        routes={Routes.webApi.form.testing}
        onSuccess={data => {
          getInjection('PulseAnalytics').track('event_signup', {
            eventName: 'Функциональное тестирование',
            ...data,
          })
        }}
      />
    </FormProvider>
  )

  const steps: FormStepConfig[] = [
    {
      step: FormTestingStep.SelectTypeTesting,
      title: 'Тип тестирования',
      summary: types.find(type => type.id === testingFormStore.typeId)?.title,
      filled: !!testingFormStore.typeId,
      content: typeStepContent,
    },
    {
      step: FormTestingStep.SelectLab,
      title: 'Лаборатория',
      summary: (() => {
        const lab = labs.find(lab => lab.id === testingFormStore.shopId)
        return lab ? `${lab.title}, ${lab.address}` : undefined
      })(),
      filled: !!testingFormStore.shopId,
      content: labStepContent,
    },
    {
      step: FormTestingStep.SelectDay,
      title: 'Дата и время',
      summary:
        testingFormStore.date && testingFormStore.time
          ? `${testingFormStore.date.toDate().toLocaleDateString()} в ${testingFormStore.time.title}`
          : undefined,
      filled: !!testingFormStore.date && !!testingFormStore.time,
      content: dateStepContent,
    },
    {
      step: FormTestingStep.Finish,
      title: 'Данные для записи',
      filled: false,
      content: personalStepContent,
    },
  ]

  if (isMobile) {
    return (
      <Wrapper>
        <FormStepsAccordion steps={steps} />
      </Wrapper>
    )
  }

  return (
    <Wrapper>
      {typeStepContent}

      {testingFormStore.step > FormTestingStep.SelectTypeTesting && labStepContent}

      {testingFormStore.step > FormTestingStep.SelectLab && dateStepContent}

      {testingFormStore.step > FormTestingStep.SelectDay && personalStepContent}
    </Wrapper>
  )
})

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 36px;
  gap: 36px;
`
